import React, { useEffect, useCallback, useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { centerActivityActions } from "../../../../../redux/actions/centerActivity.action";
import { Button, Container, Col, Form, Row } from "react-bootstrap";
import { allSharedComponents } from "../../../../../shared";
import {
  getUserPayload,
  convertObjectToFormData,
  mergeDateTimeToServerFormat,
  serverFormatToDateObject,
  UTCWithTimezoneToISOString,
  dateToUTCWithTimezone,
  replaceLocalToUTCFormatZ,
} from "../../../../../system";
import {
  translationConstants,
  modeConstants,
  Directions,
  centerActivityStatusEnum,
  centerActivityConstants,
  globalConstants,
  fieldLengthConstants,
  Regex,
  EVENT_STATUS,
  competitionConstants,
  timezoneConstants,
} from "../../../../../constants";
import ConfirmationModal from "../../../../../shared/components/Modals/ConfirmationModal";
import CustomModal from "../../../../../shared/components/CustomModal/CustomModal";

const CenterActivityForm = (props) => {
  const { Mandatory_Character } = globalConstants;

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const {
    FileUploader,
    ReactDatePickerWrapper,
    InputFieldWrapper,
    SpinnerContainer,
  } = allSharedComponents;

  const {
    setValue,
    getValues,
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm();

  const activityDataById = useSelector(
    (state) => state.centerActivityReducer.activityDataById
  );
  const spotLocations = useSelector(
    (state) => state.SpotsReducer.spotDataByCenterId.data
  );
  const direction = useSelector((state) => {
    return state.LanguageChangeReducer.direction;
  });

  const [posterValidationMessage, setPosterValidationMessage] = useState("");
  const [logoValidationMessage, setLogoValidationMessage] = useState("");
  const [isLogoChanged, setIsLogoChanged] = useState(false);
  const [isPostersChanged, setIsPostersChanged] = useState(false);
  const [logo, setLogo] = useState([]);
  const [posters, setPosters] = useState([]);
  const [modalEvent, setModalEvent] = useState({});
  const [modalData, setModalData] = useState({});
  const [isModalOpen, setModalOpen] = useState(false);

  const onSubmit = (data, event) => {
    const { endTime, startTime, ...removedTime } = data;
    const buttonId = event?.nativeEvent?.submitter?.id;
    const userData = getUserPayload();

    const dataForUpdate = {
      ...removedTime,
      centerId: userData.user.centerId,
      startDateString: mergeDateTimeToServerFormat(
        removedTime.startDateString,
        startTime
      ),
      endDateString: mergeDateTimeToServerFormat(
        removedTime.endDateString,
        endTime ? endTime : moment().set(timezoneConstants.twelvePM).toDate()
      ),
      ...(removedTime?.posters?.length > 0
        ? { posters: removedTime.posters[0] }
        : {}),
      ...(removedTime?.logo?.length > 0 ? { logo: removedTime.logo[0] } : {}),
      isActive: true,
      isPostersUpdated: isPostersChanged,
      isLogoUpdated: isLogoChanged,
    };

    const {
      posters,
      logo,
      isPostersUpdated,
      isLogoUpdated,
      ...removedPosterLogo
    } = dataForUpdate;

    const dataForAdd = {
      centerActivityInfo: {
        ...removedPosterLogo,
      },
      posters: posters,
      logo: logo,
    };

    const formData = convertObjectToFormData(dataForAdd);

    if (props.mode === modeConstants.ADD_MODE) {
      if (buttonId === centerActivityConstants.ADD_BUTTON) {
        // Dispatch action for creating and publishing
        formData.append(
          "centerActivityInfo.status",
          centerActivityStatusEnum.PUBLISHED
        );
        dispatch(centerActivityActions.addActivity(formData, props.gridState));
      } else if (buttonId === centerActivityConstants.DRAFT_BUTTON) {
        // Dispatch action for saving as draft
        formData.append(
          "centerActivityInfo.status",
          centerActivityStatusEnum.DRAFT
        );
        dispatch(centerActivityActions.addActivity(formData, props.gridState));
      }
    } else if (props.mode === modeConstants.EDIT_MODE) {
      if (buttonId === centerActivityConstants.UNPUBLISH_BUTTON) {
        // Dispatch action for unpublishing
        const statusData = {
          id: activityDataById.id,
          status: centerActivityStatusEnum.UNPUBLISHED,
        };
        dispatch(
          centerActivityActions.updateActivityStatusById(
            statusData,
            activityDataById.id,
            props.gridState
          )
        );
      } else if (buttonId === centerActivityConstants.PUBLISH_BUTTON) {
        // Dispatch action for publishing
        dispatch(
          centerActivityActions.publishActivity(
            activityDataById.id,
            props.gridState
          )
        );
      } else if (buttonId === centerActivityConstants.UPDATE_BUTTON) {
        const formData = convertObjectToFormData(dataForUpdate);
        formData.append("id", activityDataById?.id);
        formData.append("status", activityDataById?.centerActivityInfo?.status);
        dispatch(
          centerActivityActions.updateActivity(formData, props.gridState)
        );
      }
    }
    props.setShow(false);
  };

  useEffect(() => {
    if (
      props.mode === modeConstants.VIEW_MODE ||
      props.mode === modeConstants.EDIT_MODE
    ) {
      //Set initial form values from activityDataById if they are defined
      const tinyInfo = activityDataById.centerActivityInfo;
      tinyInfo?.centerSpotId &&
        setValue("centerSpotId", tinyInfo?.centerSpotId);
      tinyInfo?.name && setValue("name", tinyInfo?.name);
      setValue(
        "arabicName",
        tinyInfo?.arabicName ?? globalConstants.EMPTY_STRING
      );
      tinyInfo?.description && setValue("description", tinyInfo?.description);
      setValue(
        "arabicDescription",
        tinyInfo?.arabicDescription ?? globalConstants.EMPTY_STRING
      );
      tinyInfo?.url && setValue("url", tinyInfo?.url);

      tinyInfo?.startDateString &&
        setValue(
          "startDateString",
          serverFormatToDateObject(tinyInfo?.startDateString)
        );
      tinyInfo?.endDateString &&
        setValue(
          "endDateString",
          serverFormatToDateObject(tinyInfo?.endDateString)
        );
      tinyInfo?.startDateString &&
        setValue(
          "startTime",
          serverFormatToDateObject(tinyInfo?.startDateString)
        );
      tinyInfo?.endDateString &&
        setValue("endTime", serverFormatToDateObject(tinyInfo?.endDateString));

      if (props.mode !== modeConstants.ADD_MODE) {
        if (activityDataById?.logo) {
          setLogo([activityDataById.logo]);
        } else {
          setLogo([]);
        }
        if (activityDataById?.posters) {
          setPosters(activityDataById?.posters);
        } else {
          setPosters([]);
        }
      }
    }
    // eslint-disable-next-line
  }, [dispatch, activityDataById]);

  const handleFileUpload = useCallback(
    (file, key) => {
      setValue(key, file, { shouldValidate: true });
    },
    [setValue]
  );

  // Validation for endDateString (should be the same as startDateString or later)
  const isAfterOrEqualStartDate = (endDateString) => {
    const startDateString = getValues("startDateString");
    return (
      replaceLocalToUTCFormatZ(endDateString).isSameOrAfter(
        replaceLocalToUTCFormatZ(startDateString).startOf("day")
      ) || t(translationConstants.CENTER_END_DATE_NOT_BEFORE_START_DATE)
    );
  };

  // Validation for startTime (if startDateString is today, startTime should not be in the past)
  const isAfterOrEqualCurrentTimeIfToday = (startTime) => {
    const startDateString = getValues("startDateString");
    return (
      !replaceLocalToUTCFormatZ(startDateString).isSame(
        dateToUTCWithTimezone(),
        "day"
      ) ||
      replaceLocalToUTCFormatZ(startTime).isSameOrAfter(
        dateToUTCWithTimezone()
      ) ||
      t(translationConstants.CENTER_START_TIME_NOT_BEFORE_CURRENT_TIME)
    );
  };

  // Validation for endTime (if endDateString is the same as startDateString, endTime should be after startTime)
  const isAfterStartTimeIfSameDay = (endTime) => {
    const startDateString = getValues("startDateString");
    const endDateString = getValues("endDateString");
    const startTime = getValues("startTime");

    const endTimeUTC = replaceLocalToUTCFormatZ(endTime);
    const startTimeUTC = replaceLocalToUTCFormatZ(startTime);

    // Compare only the time
    const isEndTimeAfterStartTime =
      endTimeUTC.hour() > startTimeUTC.hour() ||
      (endTimeUTC.hour() === startTimeUTC.hour() &&
        endTimeUTC.minute() > startTimeUTC.minute());
    return (
      !endTime ||
      !replaceLocalToUTCFormatZ(endDateString).isSame(
        replaceLocalToUTCFormatZ(startDateString),
        "day"
      ) ||
      isEndTimeAfterStartTime ||
      t(translationConstants.CENTER_END_TIME_NOT_BEFORE_START_TIME)
    );
  };

  const dateLocationHandler = (data, event) => {
    if (
      !(activityDataById.centerActivityInfo.status === EVENT_STATUS.Draft) &&
      !(
        event?.nativeEvent?.submitter?.id ===
          competitionConstants.UNPUBLISH_BUTTON ||
        event?.nativeEvent?.submitter?.id ===
          competitionConstants.PUBLISH_BUTTON
      )
    ) {
      setModalData(data);
      setModalEvent(event);
      setModalOpen(!isModalOpen);
    } else {
      onSubmit(data, event);
    }
  };

  return (
    <section>
      <div className="ViewHeader">
        <p className="viewUserPara">
          {props.mode === modeConstants.EDIT_MODE
            ? t(translationConstants.EDIT_CENTER_ACTIVITY)
            : props.mode === modeConstants.ADD_MODE
            ? t(translationConstants.ADD_CENTER_ACTIVITY)
            : t(translationConstants.VIEW_CENTER_ACTIVITY)}
        </p>
      </div>
      <p className="horizentalLine" />
      <div className="m-t-24">
        <Form
          noValidate
          className="ViewUserContent ViewUserContentForm"
          onSubmit={
            props.mode === modeConstants.ADD_MODE
              ? handleSubmit(onSubmit)
              : handleSubmit(dateLocationHandler)
          }
        >
          <Row>
            <InputFieldWrapper
              label={
                `${t(translationConstants.CENTER_ACTIVITY_NAME)}:` +
                Mandatory_Character
              }
            >
              <Form.Control
                type="text"
                id="name"
                maxLength={fieldLengthConstants.TEXT_FIELD_LENGTH}
                disabled={props.mode === modeConstants.VIEW_MODE}
                placeholder={t(translationConstants.ENTER_CENTER_ACTIVITY_NAME)}
                {...register("name", {
                  required: t(
                    translationConstants.CENTER_ACTIVITY_NAME_REQUIRED
                  ),
                  maxLength: {
                    value: fieldLengthConstants.TEXT_FIELD_LENGTH,
                    message: t(
                      translationConstants.CENTER_ACTIVITY_NAME_TOO_LONG
                    ),
                  },
                  pattern: {
                    value:
                      /^[A-Za-z0-9\s!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]+$/i,
                    message: t(
                      translationConstants.INVALID_CENTER_ACTIVITY_NAME
                    ),
                  },
                })}
              />
              {errors?.name && (
                <p className="errorText">{errors?.name.message}</p>
              )}
            </InputFieldWrapper>
            <InputFieldWrapper
              label={
                <>
                  {t(translationConstants.CENTER_ACTIVITY_NAME)} (
                  {t(translationConstants.ARABIC)}):{" "}
                  <span className="greenText">
                    {t(translationConstants.OPTIONAL)}
                  </span>
                </>
              }
            >
              <Form.Control
                id="arabicName"
                dir={Directions.rtl}
                type="text"
                maxLength={fieldLengthConstants.TEXT_FIELD_LENGTH}
                disabled={props.mode === modeConstants.VIEW_MODE}
                placeholder="أدخل اسم النشاط"
                {...register("arabicName", {
                  maxLength: {
                    value: fieldLengthConstants.TEXT_FIELD_LENGTH,
                    message: t(
                      translationConstants.CENTER_ACTIVITY_ARABIC_NAME_TOO_LONG
                    ),
                  },
                })}
              />
              {errors?.arabicName && (
                <p className="errorText">{errors?.arabicName.message}</p>
              )}
            </InputFieldWrapper>

            <InputFieldWrapper
              lg={3}
              label={
                t(translationConstants.CENTER_START_DATE) + Mandatory_Character
              }
            >
              <Controller
                name="startDateString"
                control={control}
                rules={{
                  required: t(translationConstants.CENTER_START_DATE_REQUIRED),
                }}
                render={({ field }) => (
                  <div
                    className={`react-datepicker-wrapper-parent ${
                      props.mode === modeConstants.VIEW_MODE && "disabled-bg"
                    }`}
                  >
                    <ReactDatePickerWrapper
                      onChange={(date) => {
                        field.onChange(date);
                      }}
                      selected={field.value}
                      minDate={new Date(UTCWithTimezoneToISOString())}
                      value={field.value}
                      disabled={props.mode === modeConstants.VIEW_MODE}
                    />
                  </div>
                )}
              />
              {errors?.startDateString && (
                <p className="errorText">{errors?.startDateString.message}</p>
              )}
            </InputFieldWrapper>

            <InputFieldWrapper
              lg={3}
              label={
                t(translationConstants.CENTER_END_DATE) + Mandatory_Character
              }
            >
              <Controller
                name="endDateString"
                control={control}
                rules={{
                  required: t(translationConstants.CENTER_END_DATE_REQUIRED),
                  validate: {
                    isAfterOrEqualStartDate: isAfterOrEqualStartDate,
                  },
                }}
                render={({ field }) => (
                  <div
                    className={`react-datepicker-wrapper-parent ${
                      props.mode === modeConstants.VIEW_MODE && "disabled-bg"
                    }`}
                  >
                    <ReactDatePickerWrapper
                      onChange={(date) => {
                        field.onChange(date);
                      }}
                      selected={field.value}
                      minDate={new Date(UTCWithTimezoneToISOString())}
                      value={field.value}
                      disabled={props.mode === modeConstants.VIEW_MODE}
                    />
                  </div>
                )}
              />
              {errors?.endDateString && (
                <p className="errorText">{errors?.endDateString.message}</p>
              )}
            </InputFieldWrapper>

            <InputFieldWrapper
              lg={3}
              label={
                t(translationConstants.CENTER_START_TIME) + Mandatory_Character
              }
            >
              <Controller
                name="startTime"
                control={control}
                rules={
                  props.mode === modeConstants.ADD_MODE
                    ? {
                        required: t(
                          translationConstants.CENTER_START_TIME_REQUIRED
                        ),
                        validate: {
                          isAfterOrEqualCurrentTimeIfToday:
                            isAfterOrEqualCurrentTimeIfToday,
                        },
                      }
                    : {}
                }
                render={({ field }) => (
                  <div
                    className={`react-datepicker-wrapper-parent ${
                      props.mode === modeConstants.VIEW_MODE && "disabled-bg"
                    }`}
                  >
                    <ReactDatePickerWrapper
                      onChange={(time) => {
                        field.onChange(time);
                      }}
                      isTimePicker={true}
                      selected={field.value}
                      value={field.value}
                      disabled={props.mode === modeConstants.VIEW_MODE}
                    />
                  </div>
                )}
              />
              {errors?.startTime && (
                <p className="errorText">{errors?.startTime.message}</p>
              )}
            </InputFieldWrapper>
            <InputFieldWrapper
              lg={3}
              label={
                <>
                  {t(translationConstants.CENTER_END_TIME)}:{" "}
                  <span className="greenText">
                    {t(translationConstants.OPTIONAL)}
                  </span>
                </>
              }
            >
              <Controller
                name="endTime"
                control={control}
                rules={{
                  validate: {
                    isAfterStartTimeIfSameDay: isAfterStartTimeIfSameDay,
                  },
                }}
                render={({ field }) => (
                  <div
                    className={`react-datepicker-wrapper-parent ${
                      props.mode === modeConstants.VIEW_MODE && "disabled-bg"
                    }`}
                  >
                    <ReactDatePickerWrapper
                      onChange={(time) => {
                        field.onChange(time);
                      }}
                      isTimePicker={true}
                      selected={field.value}
                      value={field.value}
                      disabled={props.mode === modeConstants.VIEW_MODE}
                    />
                  </div>
                )}
              />
              {errors?.endTime && (
                <p className="errorText">{errors?.endTime.message}</p>
              )}
            </InputFieldWrapper>
            <div className="activity-modal-timezone">
              {t(translationConstants.TIMEZONE_DISPLAY_ON_GRID)}
            </div>
            <InputFieldWrapper
              label={
                `${t(translationConstants.ACTIVITY_LINK)}:` +
                Mandatory_Character
              }
            >
              <Form.Control
                type="text"
                id="url"
                disabled={props.mode === modeConstants.VIEW_MODE}
                placeholder={t(translationConstants.ENTER_CENTER_ACTIVITY_LINK)}
                {...register("url", {
                  required: t(
                    translationConstants.CENTER_ACTIVITY_LINK_REQUIRED
                  ),
                  pattern: {
                    value: Regex.urlRegex,
                    message: t(translationConstants.URL_VALIDATION),
                  },
                })}
              />
              {errors?.url && (
                <p className="errorText">{errors?.url.message}</p>
              )}
            </InputFieldWrapper>
            <InputFieldWrapper
              label={
                `${t(translationConstants.CENTER_ACTIVITY_LOCATION)}:` +
                Mandatory_Character
              }
            >
              <Form.Select
                id="centerSpotId"
                disabled={props.mode === modeConstants.VIEW_MODE}
                {...register("centerSpotId", {
                  required: t(
                    translationConstants.CENTER_ACTIVITY_LOCATION_REQUIRED
                  ),
                })}
              >
                <option value="">
                  {t(translationConstants.SELECT_CENTER_ACTIVITY_LOCATION)}
                </option>
                {spotLocations?.map((spotLocations) => (
                  <option
                    key={spotLocations.spotId}
                    value={spotLocations.centerSpotId}
                  >
                    {direction === Directions.rtl
                      ? spotLocations?.arabicSpotName || spotLocations?.spotName
                      : spotLocations?.spotName}
                  </option>
                ))}
              </Form.Select>
              {errors?.centerSpotId && (
                <p className="errorText">{errors?.centerSpotId.message}</p>
              )}
            </InputFieldWrapper>
            <InputFieldWrapper
              md={12}
              label={
                <>
                  {t(translationConstants.CENTER_ACTIVITY_DESCRIPTION)}:
                  {Mandatory_Character}
                </>
              }
            >
              <Form.Control
                type="text"
                id="description"
                as="textarea"
                disabled={props.mode === modeConstants.VIEW_MODE}
                rows={2}
                placeholder={t(
                  translationConstants.ENTER_CENTER_ACTIVITY_DESCRIPTION
                )}
                {...register("description", {
                  required: t(
                    translationConstants.CENTER_ACTIVITY_DESCRIPTION_REQUIRED
                  ),
                })}
              />
              {errors?.description && (
                <p className="errorText">{errors?.description.message}</p>
              )}
            </InputFieldWrapper>
            <InputFieldWrapper
              md={12}
              label={
                <>
                  {t(translationConstants.CENTER_ACTIVITY_DESCRIPTION)}: (
                  {t(translationConstants.ARABIC)}){" "}
                  <span className="greenText">
                    {t(translationConstants.OPTIONAL)}
                  </span>
                </>
              }
            >
              <Form.Control
                dir={Directions.rtl}
                type="text"
                id="arabicDescription"
                as="textarea"
                disabled={props.mode === modeConstants.VIEW_MODE}
                rows={2}
                placeholder={"أدخل وصف المركز"}
                {...register("arabicDescription", {})}
              />
              {errors?.arabicDescription && (
                <p className="errorText">
                  {errors?.arabicDescription?.message}
                </p>
              )}
            </InputFieldWrapper>
            <InputFieldWrapper
              lg={6}
              md={12}
              label={
                <>
                  {t(translationConstants.UPLOAD_POSTER_IMAGE)}:{" "}
                  {Mandatory_Character}
                </>
              }
            >
              <div className="filterUploader">
                <FileUploader
                  sm
                  single
                  isImage
                  disabled={props.mode === modeConstants.VIEW_MODE}
                  defaultValue={
                    posters // Do not pass anything
                  }
                  {...register("posters", {
                    required:
                      props.mode === modeConstants.EDIT_MODE
                        ? posters.length < 1 &&
                          t(translationConstants.ACTIVITY_POSTER_REQUIRED)
                        : t(translationConstants.ACTIVITY_POSTER_REQUIRED),
                  })}
                  onFileUpload={(file) => handleFileUpload(file, "posters")}
                  setValidationErrorMessage={setPosterValidationMessage}
                  onChangeFlag={setIsPostersChanged}
                  isError={errors.posters || posterValidationMessage}
                  isAllowPreview={props.mode === modeConstants.VIEW_MODE}
                  onFileRemove={() => {
                    setPosters([]);
                  }}
                />
                <p className="RecommendationText">
                  {t(translationConstants.POSTER_RECOMMENDED_SIZE)}
                </p>

                {errors.posters && !posterValidationMessage && (
                  <p className="errorText">{errors.posters.message}</p>
                )}
                {posterValidationMessage && (
                  <p className="errorText">{posterValidationMessage}</p>
                )}
              </div>
            </InputFieldWrapper>
            <InputFieldWrapper
              lg={6}
              md={12}
              label={
                <>
                  {t(translationConstants.UPLOAD_LOGO)}: {Mandatory_Character}
                </>
              }
            >
              <div className="filterUploader">
                <FileUploader
                  sm
                  single
                  isImage
                  disabled={props.mode === modeConstants.VIEW_MODE}
                  defaultValue={
                    logo // Do not pass anything
                  }
                  {...register("logo", {
                    required:
                      props.mode === modeConstants.EDIT_MODE
                        ? logo.length < 1 &&
                          t(translationConstants.ACTIVITY_LOGO_REQUIRED)
                        : t(translationConstants.ACTIVITY_LOGO_REQUIRED),
                  })}
                  onFileUpload={(file) => handleFileUpload(file, "logo")}
                  setValidationErrorMessage={setLogoValidationMessage}
                  onChangeFlag={setIsLogoChanged}
                  isError={errors.logo || logoValidationMessage}
                  isAllowPreview={props.mode === modeConstants.VIEW_MODE}
                  onFileRemove={() => {
                    setLogo([]);
                  }}
                />
                {errors.logo && !logoValidationMessage && (
                  <p className="errorText">{errors.logo.message}</p>
                )}
                {logoValidationMessage && (
                  <p className="errorText">{logoValidationMessage}</p>
                )}
              </div>
            </InputFieldWrapper>
          </Row>
          {props.mode === modeConstants.EDIT_MODE ? (
            <Row>
              <Col
                md={12}
                className="d-block d-sm-flex justify-content-center mt-2"
              >
                <Row>
                  {activityDataById?.centerActivityInfo?.status ===
                  centerActivityStatusEnum.PUBLISHED ? (
                    <Col className="d-flex justify-content-center">
                      <Button
                        id={centerActivityConstants.UNPUBLISH_BUTTON}
                        className="btn-submit-outline mx-2 mb-2 mb-lg-0 "
                        type="submit"
                      >
                        {t(translationConstants.UNPUBLISH)}
                      </Button>
                    </Col>
                  ) : (
                    <Col className="d-flex justify-content-center">
                      <Button
                        id={centerActivityConstants.PUBLISH_BUTTON}
                        className="btn-submit mx-2 mb-2 mb-lg-0 "
                        type="submit"
                      >
                        {t(translationConstants.PUBLISH)}
                      </Button>
                    </Col>
                  )}
                  <Col className="d-flex justify-content-center">
                    <Button
                      id={centerActivityConstants.UPDATE_BUTTON}
                      type="submit"
                      className="btn-submit mx-2"
                    >
                      {t(translationConstants.Update)}
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
          ) : props.mode === modeConstants.ADD_MODE ? (
            <Row className="d-flex justify-content-center mt-2 mx-lg-5">
              <Col className="mx-1 d-flex justify-content-center">
                <Button
                  id={centerActivityConstants.ADD_BUTTON}
                  className="btn-submit mb-2"
                  type="submit"
                >
                  {t(translationConstants.CREATE_AND_PUBLISH)}
                </Button>
              </Col>
              <Col className="mx-1 d-flex justify-content-center">
                <Button
                  id={centerActivityConstants.DRAFT_BUTTON}
                  className="btn-submit mb-2"
                  type="submit"
                >
                  {t(translationConstants.SAVE_DRAFT)}
                </Button>
              </Col>
              <Col className="mx-1 d-flex justify-content-center">
                <Button
                  className="reject-button m-0"
                  type="button"
                  onClick={() => props.setShow(false)}
                >
                  {t(translationConstants.cancel)}
                </Button>
              </Col>
            </Row>
          ) : (
            ""
          )}
        </Form>
      </div>
      <CustomModal
        scrollable={true}
        size="md"
        setShow={dateLocationHandler}
        show={isModalOpen}
        customClass="view-user-modal"
      >
        <ConfirmationModal
          title={t(translationConstants.CONFIRMATION)}
          messageLine1={""}
          messageLine2={t(translationConstants.are_you_sure_you_want_to_update)}
          onCancel={() => setModalOpen(!isModalOpen)}
          onSubmit={() => {
            onSubmit(modalData, modalEvent);
            setModalOpen(!isModalOpen);
          }}
        />
      </CustomModal>
    </section>
  );
};

export default CenterActivityForm;
